import type { ProductProjection } from "@commercetools/platform-sdk";
import { isString } from "@sindresorhus/is";
import { useQuery } from "@tanstack/react-query";
import { useAppState } from "../context/app/context";
import { fetchFromCTWithSession } from "../utils/commercetools/frontend-session";

const key = process.env.GATSBY_COMMERCETOOLS_PROJECT_KEY;
const language = process.env.GATSBY_LANGUAGE;
const store = process.env.GATSBY_STORE;

if (!key || !language || !store) {
	throw new Error(
		"Missing required environment variables: GATSBY_COMMERCETOOLS_PROJECT_KEY, GATSBY_LANGUAGE, or GATSBY_STORE",
	);
}

async function getProductProjectionById(id: string, currency: string) {
	const filter = `id:"${id}"`;
	const query = new URLSearchParams({
		filter,
		staged: "false",
		storeProjection: store as string,
		localeProjection: language as string,
		priceCurrency: currency,
	});

	const { data, error } = await fetchFromCTWithSession<{
		results: ProductProjection[];
	}>(`/${key}/product-projections/search?${query.toString()}`);

	if (error) {
		throw error;
	}

	return data?.results[0];
}

interface UseVariantInventoryParams {
	commercetoolsProductId: string | undefined;
	sku: string | null | undefined;
}

export function useVariantInventory({
	commercetoolsProductId,
	sku,
}: UseVariantInventoryParams) {
	const { siteConfig } = useAppState();

	const {
		data: product,
		status,
		error,
	} = useQuery({
		queryKey: ["product-projection", commercetoolsProductId],
		queryFn: () =>
			getProductProjectionById(commercetoolsProductId!, siteConfig.currency),
		enabled: isString(commercetoolsProductId),
		staleTime: 1000 * 60 * 5, // 5 minutes
	});

	const variant =
		product?.masterVariant.sku === sku
			? product?.masterVariant
			: product?.variants.find((v) => v.sku === sku);

	const inventory = variant?.availability?.channels
		? Object.values(variant.availability.channels)[0]
		: variant?.availability;

	return {
		inventory,
		status,
		error,
	};
}
